import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Modal, Form, Input, Select, Space, Popconfirm, Tag, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, LockOutlined } from '@ant-design/icons';
import { getUsers, createUser, updateUser, deleteUser, setUserStatus, resetUserPassword, getRoles } from '../../../services/api';

export default function UserList() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [roles, setRoles] = useState([]);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getUsers({ page, pageSize: 10 });
      setData(res.data.list);
      setTotal(res.data.total);
    } catch (err) { message.error(err.message); }
    setLoading(false);
  };

  const fetchRoles = async () => {
    try {
      const res = await getRoles();
      setRoles(res.data || []);
    } catch {}
  };

  useEffect(() => { fetchData(); fetchRoles(); }, [page]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editRecord) {
        await updateUser(editRecord.id, values);
      } else {
        await createUser(values);
      }
      message.success('保存成功');
      setModalOpen(false);
      fetchData();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.message);
    }
  };

  const openModal = (record) => {
    setEditRecord(record);
    setModalOpen(true);
  };

  useEffect(() => {
    if (modalOpen) {
      form.resetFields();
      form.setFieldsValue(editRecord || { status: 1 });
    }
  }, [modalOpen]);

  const handleDelete = async (id) => {
    try {
      await deleteUser(id);
      message.success('删除成功');
      fetchData();
    } catch (err) { message.error(err.message); }
  };

  const handleStatus = async (id, status) => {
    try {
      await setUserStatus(id, status);
      fetchData();
    } catch (err) { message.error(err.message); }
  };

  const handleResetPwd = async (id) => {
    try {
      const res = await resetUserPassword(id);
      message.success(res.message);
    } catch (err) { message.error(err.message); }
  };

  const columns = [
    { title: '用户名', dataIndex: 'username' },
    { title: '昵称', dataIndex: 'nickname' },
    { title: '手机号', dataIndex: 'phone' },
    { title: '角色', dataIndex: ['role', 'name'] },
    { title: '状态', dataIndex: 'status', render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? '启用' : '禁用'}</Tag> },
    { title: '最后登录', dataIndex: 'lastLoginAt', render: (v) => v ? new Date(v).toLocaleString() : '-' },
    { title: '操作', render: (_, r) => (
      <Space>
        <Button size="small" icon={<EditOutlined />} onClick={() => openModal(r)}>编辑</Button>
        <Button size="small" icon={<LockOutlined />} onClick={() => handleResetPwd(r.id)}>重置密码</Button>
        {r.status ? (
          <Button size="small" danger onClick={() => handleStatus(r.id, 0)}>禁用</Button>
        ) : (
          <Button size="small" type="primary" ghost onClick={() => handleStatus(r.id, 1)}>启用</Button>
        )}
        <Popconfirm title="确定删除？" onConfirm={() => handleDelete(r.id)}>
          <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal(null)}>新增用户</Button>
      </Space>
      <Table dataSource={data} columns={columns} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize: 10, onChange: setPage }} />
      <Modal title={editRecord ? '编辑用户' : '新增用户'} open={modalOpen} onOk={handleSave} onCancel={() => setModalOpen(false)} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="username" label="用户名" rules={[{ required: true, message: '请输入用户名' }]}>
            <Input disabled={!!editRecord} />
          </Form.Item>
          {!editRecord && (
            <Form.Item name="password" label="密码" rules={[{ required: true, message: '请输入密码' }]}>
              <Input.Password />
            </Form.Item>
          )}
          <Form.Item name="nickname" label="昵称" rules={[{ required: true, message: '请输入昵称' }]}><Input /></Form.Item>
          <Form.Item name="phone" label="手机号" rules={[
            { required: true, message: '请输入手机号' },
            { pattern: /^1\d{10}$/, message: '请输入正确的11位手机号' },
          ]}><Input maxLength={11} /></Form.Item>
          <Form.Item name="roleId" label="角色" rules={[{ required: true, message: '请选择角色' }]}>
            <Select options={roles.map(r => ({ label: r.name, value: r.id }))} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={[{ label: '启用', value: 1 }, { label: '禁用', value: 0 }]} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
